import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { QuoteCalculator } from "@/components/QuoteCalculator";
import { AdminLogin } from "@/pages/AdminLogin";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { Phone, MapPin, Shield, Sparkles, Wrench, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-amber-50/30 to-orange-50/50">
      {/* Header/Navigation */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo/Brand */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                Hill Boyz On The Road
              </h1>
              <p className="text-amber-100 text-sm md:text-base">
                RV Detailing & Repair • Akron, IN
              </p>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6 mr-6">
              <button onClick={() => scrollToSection('detailing')} className="hover:text-amber-200 transition font-medium">
                Detailing
              </button>
              <button onClick={() => scrollToSection('repair')} className="hover:text-amber-200 transition font-medium">
                Repair
              </button>
              <button onClick={() => scrollToSection('quote')} className="hover:text-amber-200 transition font-medium">
                Get Quote
              </button>
              <button onClick={() => scrollToSection('contact')} className="hover:text-amber-200 transition font-medium">
                Contact
              </button>
            </nav>

            {/* Call Button */}
            <a
              href="tel:5743225652"
              className="hidden md:flex bg-white text-orange-600 px-6 py-3 rounded-lg font-bold hover:bg-amber-50 transition-all duration-300 shadow-lg hover:shadow-xl items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              (574) 322-5652
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('detailing')} className="block w-full text-left py-2 hover:text-amber-200 transition font-medium">
                Detailing Services
              </button>
              <button onClick={() => scrollToSection('repair')} className="block w-full text-left py-2 hover:text-amber-200 transition font-medium">
                Repair Services
              </button>
              <button onClick={() => scrollToSection('quote')} className="block w-full text-left py-2 hover:text-amber-200 transition font-medium">
                Get Quote
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 hover:text-amber-200 transition font-medium">
                Contact Us
              </button>
              <a
                href="tel:5743225652"
                className="block bg-white text-orange-600 px-4 py-3 rounded-lg font-bold text-center"
              >
                <Phone className="w-4 h-4 inline mr-2" />
                Call (574) 322-5652
              </a>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - DETAILING FOCUSED */}
      <section id="detailing" className="py-16 px-4 bg-gradient-to-br from-amber-50 via-orange-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full font-semibold mb-6 animate-pulse">
              <Sparkles className="w-5 h-5" />
              NOW BOOKING - Warm Weather Ready!
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Professional RV Detailing
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                We Come To You!
            </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-6">
              Full-service exterior care with simple, flat-fee pricing • Fully Licensed & Insured
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-gray-700 text-lg">
                <MapPin className="w-5 h-5 text-orange-600" />
                <span>Serving Akron, IN & Surrounding Areas</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 text-lg">
                <Shield className="w-5 h-5 text-orange-600" />
                <span>20+ Years Experience</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('quote')}
                className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-lg font-bold text-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-xl hover:shadow-2xl"
              >
                Get Instant Quote
              </button>
              <a
                href="tel:5743225652"
                className="bg-white text-orange-600 border-2 border-orange-600 px-8 py-4 rounded-lg font-bold text-xl hover:bg-orange-50 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Phone className="w-6 h-6" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Detailing Services Highlight */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Full-Service Exterior Care
          </h3>
          <p className="text-center text-gray-600 mb-10 text-lg">Every detail service includes:</p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: "🧽", title: "Deep Scrub & Wash", desc: "Thorough cleaning of all exterior surfaces" },
              { icon: "✨", title: "High-Gloss Wax & Polish", desc: "Professional grade wax for lasting shine" },
              { icon: "🛡️", title: "Premium Protective Sealant", desc: "Guards against weather and UV damage" },
              { icon: "💎", title: "Spot Free Shine", desc: "Perfect finish every single time" }
            ].map((service, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-amber-50 rounded-xl p-6 text-center shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-amber-100 hover:border-amber-300"
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h4 className="font-bold text-gray-900 text-lg mb-2">{service.title}</h4>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Calculator - PROMINENT PLACEMENT */}
      <section id="quote" className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <QuoteCalculator />
      </section>

      {/* Pricing Table */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h3>
          <p className="text-center text-gray-600 mb-8 text-lg">Flat-fee pricing based on RV type and length</p>
          <div className="overflow-hidden rounded-xl shadow-2xl border-2 border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-lg font-bold">RV Type</th>
                  <th className="px-6 py-4 text-left text-lg font-bold">Length</th>
                  <th className="px-6 py-4 text-left text-lg font-bold">Price Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">🚐 Travel Trailer</td>
                  <td className="px-6 py-4 text-gray-700">15-30 ft</td>
                  <td className="px-6 py-4 font-bold text-orange-600 text-xl">$200-300</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">🚛 5th Wheel</td>
                  <td className="px-6 py-4 text-gray-700">20-45 ft</td>
                  <td className="px-6 py-4 font-bold text-orange-600 text-xl">$400-500</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900">🚌 Motorhome</td>
                  <td className="px-6 py-4 text-gray-700">30-45 ft</td>
                  <td className="px-6 py-4 font-bold text-orange-600 text-xl">$500-700</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* RV Repair Services - SECONDARY BUT AVAILABLE */}
      <section id="repair" className="py-16 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-600 rounded-full mb-4">
              <Wrench className="w-8 h-8" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              RV Repair Services Also Available
            </h3>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              20 years of factory-trained experience from the RV Capital of the World
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Mobile Repair & Diagnostics",
                desc: "Complete on-site repair services brought to your location"
              },
              {
                title: "Routine Maintenance",
                desc: "Factory-trained maintenance to keep your RV running smoothly"
              },
              {
                title: "Emergency Service",
                desc: "24/7 emergency roadside assistance when you need it most"
              },
              {
                title: "Winterization Services",
                desc: "Professional seasonal prep to protect your investment"
              },
              {
                title: "Appliance Repair",
                desc: "Expert repair of all RV appliances and systems"
              },
              {
                title: "Electrical Systems",
                desc: "From AC units to generators, we fix it all"
              }
            ].map((service, index) => (
              <div
                key={index}
                className="bg-gray-800 border-2 border-gray-700 rounded-xl p-6 hover:border-amber-600 transition-all duration-300"
              >
                <h4 className="font-bold text-xl text-amber-500 mb-3">{service.title}</h4>
                <p className="text-gray-300">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="tel:5743225652"
              className="inline-flex items-center gap-2 bg-amber-600 text-white px-8 py-4 rounded-lg font-bold text-xl hover:bg-amber-700 transition-all duration-300 shadow-xl"
            >
              <Phone className="w-6 h-6" />
              Call for Repair Services
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-16 h-16 mx-auto mb-6 animate-pulse" />
          <h3 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Make Your RV Shine?
          </h3>
          <p className="text-xl md:text-2xl text-amber-100 mb-4">
            Professional mobile detailing • We come to you!
          </p>
          <p className="text-lg text-amber-200 mb-8">
            Warm weather is here - Book your detail today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="tel:5743225652"
              className="bg-white text-orange-600 px-10 py-5 rounded-lg font-bold text-2xl hover:bg-amber-50 transition-all duration-300 shadow-2xl hover:shadow-3xl flex items-center gap-3"
            >
              <Phone className="w-7 h-7" />
              (574) 322-5652
            </a>
            <button
              onClick={() => scrollToSection('quote')}
              className="bg-amber-700 text-white px-10 py-5 rounded-lg font-bold text-2xl hover:bg-amber-800 transition-all duration-300 shadow-2xl"
            >
              Get Your Quote
            </button>
          </div>
        </div>
      </section>

      {/* Contact/Footer */}
      <footer id="contact" className="bg-gray-900 text-gray-300 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-xl font-bold text-white mb-4">Hill Boyz On The Road</h4>
              <p className="mb-2">RV Detailing & Repair LLC</p>
              <p className="text-sm text-gray-400">Fully Licensed & Insured</p>
              <p className="text-sm text-gray-400">20+ Years Experience</p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-4">Contact Us</h4>
              <p className="mb-2">
                <a href="tel:5743225652" className="hover:text-amber-500 transition">
                  📞 (574) 322-5652
                </a>
              </p>
              <p className="mb-2">
                <a href="mailto:Hillboyzotrrvs@gmail.com" className="hover:text-amber-500 transition break-all">
                  ✉️ Hillboyzotrrvs@gmail.com
                </a>
              </p>
              <p>📍 Akron, Indiana</p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-white mb-4">Services</h4>
              <ul className="space-y-2">
                <li>✨ Mobile RV Detailing</li>
                <li>🔧 Mobile RV Repair</li>
                <li>🛡️ Preventive Maintenance</li>
                <li>🚨 24/7 Emergency Service</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-lg font-semibold text-white mb-2">
              Serving Akron, IN & Surrounding Areas
            </p>
            <p className="text-sm text-gray-400">
              © 2026 Hill Boyz On The Road RV Repair LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
